import React from 'react'

import { EntityPreview } from 'src/editor/shared/components/entity-preview'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { WebsiteTypographyGroup } from '../entity-and-website-typography-group/website-typography-group'
import { useTranslation } from 'react-i18next'
import {
  WebsiteButtonsInterface,
  WebsiteButtonTypes,
  WebsiteSettingsTypographyTypeEnum,
  WebsiteThemeButtonsInterface,
} from 'src/shared/types/website-settings-types'
import { WebsiteBorderGroup } from '../border-groups/website-border-group'
import { buttonStyleUpdaters } from 'src/editor/store/website-settings/website-settings-actions'
import { useWebsiteSettings } from 'client/store'
import { buttonAndFormFieldSelectorMap } from 'src/editor/store/website-settings/website-settings-selector'
import CommonButton from 'common/components/entities/Button/button'
import { PaddingType } from 'common/types/styleTypes'
import { useSelectedButtonColor } from 'src/editor/shared/hooks/use-theme-color/use-selected-button-color'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ButtonAndFormFieldStyleGroup } from '../../groups/button-and-form-field-style'
import { ColorPickerGroup } from '../../groups/color-picker'

interface WebsiteButtonGroupProps {
  withoutHorizontalLine?: boolean
  label?: string
  updateWebsiteStylesButtons: <Key extends keyof WebsiteButtonsInterface>(
    prop: Key,
  ) => (value: WebsiteButtonsInterface[Key]) => void
  buttonType: WebsiteButtonTypes
  colorKeys: {
    key: keyof WebsiteThemeButtonsInterface
    title: string
  }[]
}

export const WebsiteButtonGroup = ({
  withoutHorizontalLine,
  label = '',
  updateWebsiteStylesButtons,
  buttonType,
  colorKeys,
}: WebsiteButtonGroupProps) => {
  const { t } = useTranslation()
  const buttonStyle =
    (useWebsiteSettings(
      buttonAndFormFieldSelectorMap[buttonType],
    ) as WebsiteButtonsInterface) || {}

  const {
    updateWebsiteStylesButtonsTypography,
    updateWebsiteStylesButtonsBorder,
  } = buttonStyleUpdaters[buttonType]

  const { buttonTextColor, buttonBackgroundColor, buttonBorderColor } =
    useSelectedButtonColor(
      buttonType || WebsiteSettingsTypographyTypeEnum.PrimaryButton,
    )

  return (
    <>
      <GroupTitle
        className="mb-6"
        withoutHorizontalLine={withoutHorizontalLine}
      >
        {t(label)}
      </GroupTitle>
      <EntityPreview>
        <CommonButton
          attrId="test"
          text={t('website_styles_settings.text_example')}
          border={{ ...buttonStyle.border, color: buttonBorderColor }}
          padding={buttonStyle.padding as PaddingType}
          alignSelf="center"
          textFontSize={buttonStyle.typography?.fontSize || 16}
          background={buttonBackgroundColor}
          subTextFontSize={buttonStyle?.typography?.fontSize || 0}
          textColor={buttonTextColor}
          textFontFamily={buttonStyle.typography?.fontFamily}
          textFontWeight={buttonStyle.typography?.fontWeight}
          textFontStyle={buttonStyle.typography?.fontStyle}
          lineHeight={buttonStyle.typography?.lineHeight}
          width="100%"
        />
      </EntityPreview>
      <ButtonAndFormFieldStyleGroup
        updateWebsiteStyles={updateWebsiteStylesButtons}
        styleSettings={buttonStyle}
        type={buttonType}
      />
      <WebsiteBorderGroup
        stylesObject={buttonStyle as WebsiteButtonsInterface}
        updateBorderAction={updateWebsiteStylesButtonsBorder}
      />
      <SettingsNestedMenu menuButtonLabel="Typography" menuTitle="Typography">
        <WebsiteTypographyGroup
          typographyObject={buttonStyle.typography}
          updateTypographyAction={updateWebsiteStylesButtonsTypography}
          hideTitle
          hideTextAlign
          hideLetterSpacing
          isFirst
        />
      </SettingsNestedMenu>
      <SettingsNestedMenu menuButtonLabel="Colors" menuTitle="Colors">
        <ColorPickerGroup colorKeys={colorKeys} elementKey="buttons" />
      </SettingsNestedMenu>
    </>
  )
}
